'use client'

import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Phone,
  PhoneCall,
  GitBranch,
  PhoneIncoming,
  PhoneOutgoing,
  ArrowRight,
  Plus,
  Bot,
  Users,
  Voicemail,
  Play,
  Clock,
  ArrowRightLeft,
  PhoneOff,
  PhoneMissed,
  Loader2,
  Megaphone,
} from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import { useCampaignsQuery } from '@/hooks/queries/campaigns'
import { usePbxPhoneNumbersQuery, type PbxPhoneNumber } from '@/hooks/queries/pbx-phone-numbers'
import {
  usePbxCallFlowsQuery,
  usePbxCallStatsQuery,
  useRecentUnifiedCallsQuery,
} from '@/hooks/queries/pbx-overview'
import { normalizePhoneNumberForMatch } from '@/lib/voice/outgoing-call-validation'
import type { UnifiedCall } from '@/types/unified-call'
import { formatDistanceToNow } from 'date-fns'

/**
 * Format duration in seconds to mm:ss or hh:mm:ss
 */
function formatDuration(seconds?: number): string {
  if (!seconds || seconds <= 0) return '—'
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }
  return `${mins}:${String(secs).padStart(2, '0')}`
}

interface OverviewTabProps {
  onNavigateToTab: (tab: string) => void
  onEditFlow: (flowId: string) => void
  onNewFlow: () => void
  onNewCampaign?: () => void
  onMakeCall?: () => void
  onImportExternalSip: () => void
  onViewAllCalls: () => void
}

const ACTIVE_CAMPAIGN_STATUSES = new Set(['scheduled', 'submitted', 'in_progress'])

export function OverviewTab({
  onNavigateToTab,
  onEditFlow,
  onNewFlow,
  onNewCampaign,
  onMakeCall,
  onImportExternalSip,
  onViewAllCalls,
}: OverviewTabProps) {
  const { fetchWithAuth } = useAuth()
  const phoneNumbersQuery = usePbxPhoneNumbersQuery(true)
  const campaignsQuery = useCampaignsQuery()
  const callFlowsQuery = usePbxCallFlowsQuery()
  const callStatsQuery = usePbxCallStatsQuery()
  const recentCallsQuery = useRecentUnifiedCallsQuery(5)

  const [loadingRecording, setLoadingRecording] = useState<string | null>(null)

  /**
   * Play recording - fetches signed URL and opens in new tab
   */
  const playRecording = async (callId: string) => {
    setLoadingRecording(callId)
    try {
      const res = await fetchWithAuth(`/api/pbx/calls/${callId}/recording`)
      if (res.ok) {
        const data = await res.json()
        if (data.url) {
          window.open(data.url, '_blank')
        }
      } else {
        console.error('Failed to get recording URL')
      }
    } catch (error) {
      console.error('Error playing recording:', error)
    } finally {
      setLoadingRecording(null)
    }
  }

  const getPhoneFlowName = (phone: PbxPhoneNumber): string | null => {
    if (!phone.call_flow_id) return null
    const flow = callFlows.find(f => f.id === phone.call_flow_id)
    return flow?.name || null
  }

  const callFlows = callFlowsQuery.data ?? []
  const recentCalls: UnifiedCall[] = recentCallsQuery.data?.calls ?? []
  const callCounts = callStatsQuery.data?.byPhoneNumber ?? {}
  const totalCalls = recentCallsQuery.data?.total ?? callStatsQuery.data?.total ?? 0
  const phonesWithConfig = phoneNumbersQuery.data ?? []
  const pbxPhones = phonesWithConfig.filter(p => p.mode === 'pbx' && p.is_enabled)
  const pbxNumbers = pbxPhones.length
  const unassignedNumbers = phonesWithConfig.filter(p => !p.is_enabled).length
  const activeFlows = callFlows.filter(f => f.is_active).length
  const campaigns = campaignsQuery.data ?? []
  const activeCampaigns = campaigns.filter((campaign) => ACTIVE_CAMPAIGN_STATUSES.has(campaign.status)).length
  const totalCampaignRecipients = campaigns.reduce((total, campaign) => total + campaign.totalRecipients, 0)
  const latestCampaign = campaigns[0]

  if (phoneNumbersQuery.isLoading || callFlowsQuery.isLoading || callStatsQuery.isLoading || recentCallsQuery.isLoading) {
    return (
      <div className="space-y-6 pb-28 sm:pb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-32" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-28 sm:pb-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="min-h-36">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2 text-sm">
              <Phone className="h-5 w-5" />
              <span>Phone Numbers</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-medium leading-none">{phonesWithConfig.length}</div>
            <p className="mt-3 text-sm text-muted-foreground">
              {pbxNumbers} <span>with Inbound Call Flows,</span> {unassignedNumbers} <span>unassigned</span>
            </p>
          </CardContent>
        </Card>

        <Card className="min-h-36">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2 text-sm">
              <GitBranch className="h-5 w-5" />
              <span>Inbound Call Flows</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-medium leading-none">{callFlows.length}</div>
            <p className="mt-3 text-sm text-muted-foreground">
              {activeFlows} <span>active</span>
            </p>
          </CardContent>
        </Card>

        <Card className="min-h-36">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2 text-sm">
              <PhoneIncoming className="h-5 w-5" />
              <span>Total Calls</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-medium leading-none">{totalCalls}</div>
            <p className="mt-3 text-sm text-muted-foreground">
              all time
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Voicemail className="h-5 w-5 text-muted-foreground" />
            <span>Quick Actions</span>
          </CardTitle>
          <CardDescription>Common telephony tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <TooltipProvider delayDuration={700}>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    onClick={onNewFlow}
                    className="h-auto min-h-14 justify-start whitespace-normal px-4 py-3 text-left leading-tight"
                  >
                    <Plus className="mr-3 h-5 w-5 shrink-0" />
                    <span>New Inbound Call Flow</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Create a new inbound routing flow for calls, then connect phone numbers to it.
                  When this number is called, our AI will pick it up and can handle bookings, as a help desk, support or route to a human operator based on conditions.
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    onClick={onImportExternalSip}
                    className="h-auto min-h-14 justify-start whitespace-normal px-4 py-3 text-left leading-tight"
                  >
                    <PhoneOutgoing className="mr-3 h-5 w-5 shrink-0" />
                    <span>Import External Number (via SIP Trunk)</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Allows to connect your preferred IP telephony number to Versafy using SIP trunk.
                </TooltipContent>
              </Tooltip>
              {onMakeCall && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={onMakeCall}
                      className="h-auto min-h-14 justify-start whitespace-normal px-4 py-3 text-left leading-tight"
                    >
                      <PhoneCall className="mr-3 h-5 w-5 shrink-0" />
                      <span>Make a call</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Start a one-off outbound call by choosing a Voice AI Agent, caller number, recipient, and optional call instructions.
                  </TooltipContent>
                </Tooltip>
              )}
              {onNewCampaign && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={onNewCampaign}
                      className="h-auto min-h-14 justify-start whitespace-normal px-4 py-3 text-left leading-tight"
                    >
                      <Megaphone className="mr-3 h-5 w-5 shrink-0" />
                      <span>New Campaign</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Create an outbound calling campaign for a recipient list, then start or schedule it.
                    Allows to call large groups of people by your Voice AI Agent.
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        </CardContent>
      </Card>

      {/* Campaigns Tracker */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 p-4 sm:p-6">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Megaphone className="h-5 w-5 text-muted-foreground" />
              <span>Campaigns Tracker</span>
            </CardTitle>
            <CardDescription>Voice campaign activity using outbound calling</CardDescription>
          </div>
          <Badge variant={activeCampaigns > 0 ? 'default' : 'secondary'}>
            {activeCampaigns} <span>active</span>
          </Badge>
        </CardHeader>
        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-md border bg-muted/30 p-4">
              <p className="text-sm text-muted-foreground">Campaigns total</p>
              <p className="mt-2 text-3xl font-semibold leading-none">{campaigns.length}</p>
            </div>
            <div className="rounded-md border bg-muted/30 p-4">
              <p className="text-sm text-muted-foreground">Recipients total</p>
              <p className="mt-2 text-3xl font-semibold leading-none">{totalCampaignRecipients}</p>
            </div>
            <div className="rounded-md border bg-muted/30 p-4">
              <p className="text-sm text-muted-foreground">Latest campaign</p>
              {campaignsQuery.isLoading ? (
                <Skeleton className="mt-3 h-9 w-36" />
              ) : latestCampaign ? (
                <>
                  <p className="mt-2 truncate text-lg font-semibold leading-tight">{latestCampaign.name}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {latestCampaign.status.replace(/_/g, ' ')} <span>•</span> {formatDistanceToNow(new Date(latestCampaign.createdAt), { addSuffix: true })}
                  </p>
                </>
              ) : (
                <>
                  <p className="mt-2 text-lg font-semibold leading-tight">No campaigns yet</p>
                  <p className="mt-1 text-sm text-muted-foreground">Create campaigns from the Phone section</p>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PBX Phone Numbers List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Inbound Call Flow Phone Numbers</CardTitle>
            <CardDescription>Phone numbers with Inbound Call Flows enabled</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onNavigateToTab('numbers')}>
            <span>View all</span>
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {pbxPhones.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Phone className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No Inbound Call Flow numbers configured</p>
              <p className="text-sm mt-1">Enable Inbound Call Flow mode on a phone number to get started</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => onNavigateToTab('numbers')}>
                Configure Numbers
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {pbxPhones.map((phone) => {
                const flowName = getPhoneFlowName(phone)
                const callCount = callCounts[normalizePhoneNumberForMatch(phone.phone_number)] || 0

                return (
                  <div
                    key={phone.phone_number_sid}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{phone.friendly_name || phone.phone_number}</p>
                        {phone.friendly_name && phone.friendly_name !== phone.phone_number && (
                          <p className="text-sm text-muted-foreground">{phone.phone_number}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-center">
                        <p className="text-sm font-medium">{callCount}</p>
                        <p className="text-xs text-muted-foreground">calls</p>
                      </div>
                      <Badge variant="default" className="gap-1">
                        <GitBranch className="w-3 h-3" />
                        {flowName || 'Flow'}
                      </Badge>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Calls */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Recent Calls</CardTitle>
            <CardDescription>Latest inbound and outbound activity</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onViewAllCalls}>
            <span>View all</span>
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {recentCalls.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <PhoneIncoming className="mx-auto mb-3 h-10 w-10 opacity-50" />
              <p>No calls yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentCalls.map((call) => {
                const isFailed = call.status === 'failed'
                const isMissed = ['no_answer', 'missed'].includes(call.status)
                  || (call.status === 'completed' && !call.durationSeconds)
                const isOutbound = call.direction === 'outbound'
                const flowName = call.pbxData?.callFlowName
                const agentName = call.voiceAgent?.name ?? call.pbxData?.linkedAiCall?.agentName
                const recordingId = call.type === 'pbx' && call.pbxData?.recordingSid
                  ? String(call.originalId)
                  : null
                const hasDuration = typeof call.durationSeconds === 'number' && call.durationSeconds > 0

                return (
                  <div
                    key={call.id}
                    className="flex items-start justify-between gap-3 rounded-lg border p-3 transition-colors hover:bg-accent/50 sm:items-center sm:gap-4"
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isFailed ? 'bg-destructive/10' :
                        isMissed ? 'bg-yellow-100' :
                        call.type === 'voice_agent' ? 'bg-purple-100' :
                        call.pbxData?.callSegment === 'operator' ? 'bg-blue-100' :
                        call.pbxData?.callSegment === 'voicemail' ? 'bg-orange-100' :
                        'bg-muted'
                      }`}>
                        {isFailed ? (
                          <PhoneOff className="w-5 h-5 text-destructive" />
                        ) : isMissed ? (
                          <PhoneMissed className="w-5 h-5 text-yellow-600" />
                        ) : call.type === 'voice_agent' ? (
                          <Bot className="w-5 h-5 text-purple-600" />
                        ) : call.pbxData?.callSegment === 'operator' ? (
                          <Users className="w-5 h-5 text-blue-600" />
                        ) : call.pbxData?.callSegment === 'voicemail' ? (
                          <Voicemail className="w-5 h-5 text-orange-600" />
                        ) : isOutbound ? (
                          <PhoneOutgoing className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <PhoneIncoming className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="truncate font-medium">{call.callerNumber || 'Unknown caller'}</p>
                          <Badge variant="outline" className="text-xs capitalize">
                            {call.direction}
                          </Badge>
                          {call.pbxData?.handoverReason && (
                            <Badge variant="outline" className="text-xs gap-1">
                              <ArrowRightLeft className="w-3 h-3" />
                              <span>escalation</span>
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="truncate">→ {call.calledNumber || 'Unknown recipient'}</span>
                          {call.pbxData?.answeredByNumber && (
                            <span className="shrink-0 text-xs">
                              (answered: {call.pbxData.answeredByNumber})
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-0.5">
                          {flowName && (
                            <span className="text-xs text-muted-foreground">
                              <GitBranch className="w-3 h-3 inline mr-1" />
                              {flowName}
                            </span>
                          )}
                          {agentName && (
                            <span className="text-xs text-purple-600">
                              <Bot className="w-3 h-3 inline mr-1" />
                              {agentName}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="grid w-auto shrink-0 grid-cols-[auto_auto] items-center gap-x-2 gap-y-1 sm:w-[13.5rem] sm:grid-cols-[4rem_2rem_6.5rem] sm:gap-2">
                      <div className="flex justify-end sm:justify-end">
                        {hasDuration ? (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="w-4 h-4" />
                            <span className="tabular-nums">{formatDuration(call.durationSeconds ?? undefined)}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground opacity-0" aria-hidden="true">
                            <Clock className="w-4 h-4" />
                            <span className="tabular-nums">0:00</span>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-center">
                        {recordingId && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={loadingRecording === recordingId}
                            onClick={(e) => {
                              e.stopPropagation()
                              playRecording(recordingId)
                            }}
                            title="Play recording"
                          >
                            {loadingRecording === recordingId ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                      <div className="col-span-2 text-right sm:col-span-1">
                        <Badge
                          variant={
                            isFailed ? 'destructive' :
                            isMissed ? 'secondary' :
                            call.status === 'in-progress' ? 'outline' :
                            'default'
                          }
                        >
                          {isMissed ? 'missed' : call.status}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDistanceToNow(new Date(call.createdAt), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inbound Call Flows Quick Access */}
      {callFlows.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Inbound Call Flows</CardTitle>
              <CardDescription>Your configured call handling flows</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onNavigateToTab('flows')}>
              <span>View all</span>
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {callFlows.slice(0, 4).map((flow) => (
                <div
                  key={flow.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => onEditFlow(flow.id)}
                >
                  <div className="flex items-center gap-3">
                    <GitBranch className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{flow.name}</p>
                      <p className="text-xs text-muted-foreground">
                        <span>Updated</span> {formatDistanceToNow(new Date(flow.updated_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                  <Badge variant={flow.is_active ? 'default' : 'secondary'}>
                    {flow.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
